import type { SsrSuccessBreakdown } from '@atlaskit/react-ufo/ssr';

/**
 * The boolean (failure-condition) keys of {@link SsrSuccessBreakdown}. The
 * non-boolean evidence fields (`failedResources`, `failedResourceCount`,
 * `failedRouteResources`, `failedRouteResourceCount`) are excluded.
 */
export type SsrFailureReason = {
	[K in keyof SsrSuccessBreakdown]: SsrSuccessBreakdown[K] extends boolean ? K : never;
}[keyof SsrSuccessBreakdown];

/**
 * Identity helper that enforces exhaustiveness on its argument: the generic
 * constraint rejects any reason that is not an `SsrFailureReason` (typo / extra),
 * and the conditional return type collapses the parameter to `never` — failing
 * the call — unless every `SsrFailureReason` is present (omission). Adding a new
 * boolean flag to `SsrSuccessBreakdown` (owned by `@atlaskit/react-ufo`)
 * therefore breaks the build until the reason is listed below.
 */
const defineReasons = <const T extends readonly SsrFailureReason[]>(
	reasons: [SsrFailureReason] extends [T[number]] ? T : never,
): readonly SsrFailureReason[] => reasons;

/**
 * Canonical, ordered list of SSR-success failure reasons — the single source of
 * truth shared by {@link computeSsrSuccess} (SSR is successful only when none of
 * these flags is `true`) and by metric emitters that fan out a per-reason
 * counter.
 */
export const ssrFailureReasons: readonly SsrFailureReason[] = defineReasons([
	'servedStaticFallback',
	'notRendered',
	'hadRuntimeError',
	'circuitBreakerOpen',
	'hadFailedResources',
	'fetchAborted',
	'emittedSuspenseFallback',
	'hadEarlyFlushFailure',
	'hadRouteResourceFailure',
]);
